import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Check, Info } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';

export const ArcCarousel = ({
  garments,
  selectedIndex,
  onSelect,
  onCenterClick,
  tryOnCache,
  onLoadMore,
  hasMore,
  loadingMore,
}) => {
  const swiperRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current && selectedIndex !== undefined) {
      swiperRef.current.slideTo(selectedIndex);
    }
  }, [selectedIndex]);

  const handleSlideChange = (swiper) => {
    onSelect(swiper.activeIndex);
    if (hasMore && !loadingMore && swiper.activeIndex >= garments.length - 3) {
      onLoadMore();
    }
  };

  if (!garments || garments.length === 0) {
    return <div className="text-center py-8 text-slate-400">No garments available</div>;
  }

  const handleSlideClick = (index) => {
    if (index === selectedIndex) {
      onCenterClick(garments[index]);
    } else {
      if (swiperRef.current) swiperRef.current.slideTo(index);
      onSelect(index);
    }
  };

  return (
    <div className="relative">
      <Swiper
        onSwiper={(swiper) => { swiperRef.current = swiper; }}
        effect="coverflow"
        grabCursor
        centeredSlides
        slidesPerView="auto"
        initialSlide={selectedIndex || 0}
        onSlideChange={handleSlideChange}
        coverflowEffect={{ rotate: 0, stretch: 0, depth: 100, modifier: 2, slideShadows: false }}
        modules={[EffectCoverflow, Navigation]}
        className="pb-4"
      >
        {garments.map((garment, index) => {
          const isActive = index === selectedIndex;
          const productId = garment.product_id || garment.id;
          const hasCachedResult = tryOnCache[productId];

          return (
            <SwiperSlide
              key={productId || index}
              className="!w-[140px] sm:!w-[160px] md:!w-[180px]"
              onClick={() => handleSlideClick(index)}
            >
              <motion.div
                animate={{ scale: isActive ? 1 : 0.85, y: isActive ? 0 : 15, opacity: isActive ? 1 : 0.7 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                className="flex flex-col items-center cursor-pointer"
              >
                <div className={`relative rounded-xl overflow-hidden bg-white shadow-sm transition-shadow ${isActive ? 'shadow-lg ring-2 ring-blue-500/50' : ''}`}>
                  <img
                    src={garment.thumbnail_url || garment.primary_image_url || garment.images?.[0]?.image_url}
                    alt={garment.product_name || garment.name}
                    className="w-full aspect-[3/4] object-cover"
                    loading="lazy"
                  />
                  {hasCachedResult && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {isActive && (
                    <div className="absolute top-2 left-2 w-6 h-6 bg-white/80 backdrop-blur rounded-full flex items-center justify-center">
                      <Info className="w-3.5 h-3.5 text-slate-600" />
                    </div>
                  )}
                </div>
                <div className="mt-2 text-center px-1">
                  <p className="text-xs text-slate-700 font-medium truncate max-w-[130px]">
                    {garment.product_name || garment.name}
                  </p>
                  <p className="text-xs text-slate-500">
                    ₹{(garment.selling_price || garment.price || 0).toLocaleString()}
                  </p>
                </div>
              </motion.div>
            </SwiperSlide>
          );
        })}

        {loadingMore && (
          <SwiperSlide className="!w-[140px] sm:!w-[160px] md:!w-[180px]">
            <div className="flex flex-col items-center justify-center h-full py-8">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-xs text-slate-400 mt-2">Loading...</p>
            </div>
          </SwiperSlide>
        )}
      </Swiper>

      <div className="flex justify-center mt-2 gap-1">
        <span className="text-xs text-slate-400">
          {garments.length} of {hasMore ? '...' : garments.length} items
        </span>
      </div>
    </div>
  );
};
